/**
 * Native notification system — bridges Sentinel alerts to OS-level notifications.
 *
 * Desktop: Uses Tauri @tauri-apps/plugin-notification
 * Web: Uses the Web Notification API
 *
 * Triggers on:
 * - Breaking news alerts (analysis:breaking)
 * - Surge events (analysis:surge)
 * - Hotspot escalations (analysis:hotspot)
 *
 * Settings stored in localStorage:
 * - Enable/disable
 * - Minimum severity threshold
 * - Rate limiting: max 1 notification per 60 seconds
 */

import { eventBus } from '@/core/events';
import { isTauriRuntime } from '@/utils/tauri';
import type { BreakingNewsAlert } from '@/intelligence/analysis/breaking-news';
import type { SurgeEvent, HotspotEscalation } from '@/intelligence/analysis/types';

// ---------------------------------------------------------------------------
// Configuration & persistence
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'sentinel-notifications';
const RATE_LIMIT_MS = 60_000; // 1 notification per 60 seconds

export interface NotificationSettings {
  enabled: boolean;
  minSeverity: 'critical' | 'high' | 'medium';
}

const DEFAULT_SETTINGS: NotificationSettings = {
  enabled: false,
  minSeverity: 'high',
};

export function loadNotificationSettings(): NotificationSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NotificationSettings>;
      return {
        enabled: parsed.enabled ?? DEFAULT_SETTINGS.enabled,
        minSeverity: parsed.minSeverity ?? DEFAULT_SETTINGS.minSeverity,
      };
    }
  } catch { /* ignore corrupt data */ }
  return { ...DEFAULT_SETTINGS };
}

export function saveNotificationSettings(settings: NotificationSettings): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
}

// ---------------------------------------------------------------------------
// Severity comparison
// ---------------------------------------------------------------------------

const SEVERITY_ORDER: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  info: 0,
};

function meetsThreshold(severity: string, threshold: string): boolean {
  return (SEVERITY_ORDER[severity] ?? 0) >= (SEVERITY_ORDER[threshold] ?? 0);
}

// ---------------------------------------------------------------------------
// Tauri notification API (lazy-loaded)
// ---------------------------------------------------------------------------

const IS_DESKTOP = isTauriRuntime();

interface TauriNotificationModule {
  sendNotification(options: { title: string; body?: string }): void;
  isPermissionGranted(): Promise<boolean>;
  requestPermission(): Promise<'granted' | 'denied' | 'default'>;
}

let tauriNotificationModule: TauriNotificationModule | null = null;

async function getTauriNotification(): Promise<TauriNotificationModule | null> {
  if (!IS_DESKTOP) return null;
  if (tauriNotificationModule) return tauriNotificationModule;

  try {
    // Dynamic import with variable path to bypass TS module resolution —
    // this plugin may not be in node_modules during web-only development.
    const pkg = '@tauri-apps/plugin-notification';
    tauriNotificationModule = await import(/* @vite-ignore */ pkg) as TauriNotificationModule;
    return tauriNotificationModule;
  } catch {
    console.warn('[Sentinel] Tauri notification plugin not available');
    return null;
  }
}

// ---------------------------------------------------------------------------
// Permission management
// ---------------------------------------------------------------------------

export async function requestNotificationPermission(): Promise<boolean> {
  if (IS_DESKTOP) {
    const tauri = await getTauriNotification();
    if (!tauri) return false;

    const granted = await tauri.isPermissionGranted();
    if (granted) return true;

    const result = await tauri.requestPermission();
    return result === 'granted';
  }

  // Web Notification API
  if (!('Notification' in window)) return false;

  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function isNotificationSupported(): boolean {
  if (IS_DESKTOP) return true;
  return 'Notification' in window;
}

// ---------------------------------------------------------------------------
// Send notification (platform-abstracted)
// ---------------------------------------------------------------------------

async function sendNativeNotification(title: string, body: string): Promise<void> {
  if (IS_DESKTOP) {
    const tauri = await getTauriNotification();
    if (tauri) {
      tauri.sendNotification({ title, body });
      return;
    }
  }

  // Web Notification API fallback
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/sentinel-icon.png',
      tag: 'sentinel-alert', // Replaces existing notification
    });
  }
}

// ---------------------------------------------------------------------------
// Rate limiter
// ---------------------------------------------------------------------------

let lastNotificationTime = 0;

function isRateLimited(): boolean {
  const now = Date.now();
  if (now - lastNotificationTime < RATE_LIMIT_MS) return true;
  lastNotificationTime = now;
  return false;
}

// ---------------------------------------------------------------------------
// Event handlers
// ---------------------------------------------------------------------------

function handleBreakingNews(alert: BreakingNewsAlert): void {
  const settings = loadNotificationSettings();
  if (!settings.enabled) return;
  if (!meetsThreshold(alert.severity, settings.minSeverity)) return;
  if (isRateLimited()) return;

  const severityLabel = alert.severity.toUpperCase();
  sendNativeNotification(
    `[${severityLabel}] Breaking: ${alert.source}`,
    alert.title,
  );
}

function handleSurge(surge: SurgeEvent): void {
  const settings = loadNotificationSettings();
  if (!settings.enabled) return;
  if (!meetsThreshold('high', settings.minSeverity)) return;
  if (isRateLimited()) return;

  sendNativeNotification(
    'Surge Alert',
    `${surge.sourceType}: ${surge.count} events (${surge.ratio.toFixed(1)}x baseline)${surge.region ? ` in ${surge.region}` : ''}`,
  );
}

function handleHotspot(escalation: HotspotEscalation): void {
  const settings = loadNotificationSettings();
  if (!settings.enabled) return;

  // Only notify on significant escalations (score is 0-100)
  const severity = escalation.score >= 80 ? 'critical' : escalation.score >= 60 ? 'high' : 'medium';
  if (!meetsThreshold(severity, settings.minSeverity)) return;
  if (isRateLimited()) return;

  sendNativeNotification(
    `Hotspot: ${escalation.hotspotName}`,
    `Escalation score: ${escalation.score.toFixed(0)}% (${escalation.trend})`,
  );
}

// ---------------------------------------------------------------------------
// Initialization — subscribe to event bus
// ---------------------------------------------------------------------------

let cleanupFns: (() => void)[] = [];

export function initNotifications(): void {
  // Clean up any previous subscriptions
  disposeNotifications();

  cleanupFns.push(eventBus.on('analysis:breaking', handleBreakingNews));
  cleanupFns.push(eventBus.on('analysis:surge', handleSurge));
  cleanupFns.push(eventBus.on('analysis:hotspot', handleHotspot));

  console.log('[Sentinel] Native notification system initialized');
}

export function disposeNotifications(): void {
  for (const fn of cleanupFns) fn();
  cleanupFns = [];
}
